import { Decimal } from '@snap-types/types/decimal';

import { CalculationConfig } from '../types/calculation-config';
import {
  calculateOperationCosts,
  SnapOperationCalculationResults,
  SnapOperationCostParams,
} from './operation';

export type SnapWorkOrderCostParams = {
  quantity: Decimal;
  snapOperations: SnapOperationCostParams[];
};

export type SnapWorkOrderCostResults = {
  totalCost: Decimal;
  totalMaterialCost: Decimal;
  totalLaborCost: Decimal;
  totalBurdenCost: Decimal;
  totalServiceCost: Decimal;

  unitCost: Decimal;
  unitMaterialCost: Decimal;
  unitLaborCost: Decimal;
  unitBurdenCost: Decimal;
  unitServiceCost: Decimal;
  unitVariableCosts: Decimal;

  operationResults: SnapOperationCalculationResults[];
};

export function reverseSortOperations(
  operationParams: SnapOperationCostParams[],
) {
  return operationParams.toSorted((a, b) => b.sequenceNo - a.sequenceNo);
}

export function calculateWorkOrderCosts(
  params: SnapWorkOrderCostParams,
  config: CalculationConfig,
): SnapWorkOrderCostResults {
  // sort the operations by sequence number in reverse order
  //  to work out the operation start quantities with scrap
  const operations = reverseSortOperations(params.snapOperations);

  // start with the final operation end quantity = the work order quantity
  let operationEndQuantity = params.quantity;

  if (operationEndQuantity.isZero()) {
    throw new Error('Cannot calculate work order costs with a zero quantity');
  }

  const results: SnapOperationCalculationResults[] = [];

  for (const operation of operations) {
    // calculate operation values
    const opCalculationResult = calculateOperationCosts(
      operation,
      operationEndQuantity,
    );

    results.push(opCalculationResult);

    // set the end quantity (of the prior operation)
    //  to the start quantity (of the current operation)
    operationEndQuantity = opCalculationResult.startQuantity;
  }

  // re-sort by sequence number in order
  results.reverse();

  // compute total costs from all operations
  const totalMaterialCost = Decimal.sum(
    ...results.map((operation) => operation.materialCost),
  );
  const totalLaborCost = Decimal.sum(...results.map((op) => op.laborCost));
  const totalBurdenCost = Decimal.sum(...results.map((op) => op.burdenCost));
  const totalServiceCost = Decimal.sum(...results.map((op) => op.serviceCost));

  // to get unit costs, divide the cost by the WORK ORDER end quantity (from input params)
  const unitMaterialCost = Decimal.div(totalMaterialCost, params.quantity);
  const unitLaborCost = Decimal.div(totalLaborCost, params.quantity);
  const unitBurdenCost = Decimal.div(totalBurdenCost, params.quantity);
  const unitServiceCost = Decimal.div(totalServiceCost, params.quantity);

  const unitVariableCosts = Decimal.sum(
    unitLaborCost,
    unitMaterialCost,
    unitServiceCost,
  );

  const totalCost = Decimal.sum(
    totalMaterialCost,
    totalLaborCost,
    totalBurdenCost,
    totalServiceCost,
  );
  const unitCost = Decimal.div(totalCost, params.quantity).toDecimalPlaces(
    config.decimalPlaces,
  );

  return {
    totalCost,
    totalMaterialCost,
    totalBurdenCost,
    totalLaborCost,
    totalServiceCost,

    unitCost,
    unitMaterialCost,
    unitLaborCost,
    unitBurdenCost,
    unitServiceCost,
    unitVariableCosts,

    operationResults: results,
  };
}
